import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Columns from 'components/Columns';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';
import LoadingIndicator from 'components/LoadingIndicator';

import countries from 'utils/countries-iso.json';
import provinces from 'utils/provinces.json';

import SearchDesignations from './SearchDesignations';

const EMPLOYEE_QUERY = gql(`
  query Employee($id: ID!, $departmentId: ID!) {
    user(id: $id) {
      id
      employee {
        id
        fileNumber
        organisationCode
        signature
        photo
        workEmail
        workPhoneNumber
        division {
          id
          name
        }
        designation {
          id
          name
        }
        address {
          id
          line1
          line2
          city
          province
          postalCode
          country
        }
      }
    }
    department(id: $departmentId) {
      id
      divisions {
        id
        name
      }
    }
  }
`);

const UPDATE_EMPLOYEE_MUTATION = gql(`
  mutation UpsertEmployee(
    $userId: ID!
    $fileNumber: String!
    $organisationCode: String
    $signature: Upload
    $photo: Upload
    $workEmail: String
    $workPhoneNumber: String
    $divisionId: ID!
    $designationId: ID!
    $line1: String!
    $line2: String
    $city: String!
    $postalCode: String!
    $province: String!
    $country: String!
  ) {
    upsertEmployee(
      userId: $userId
      fileNumber: $fileNumber
      organisationCode: $organisationCode
      signature: $signature
      photo: $photo
      workEmail: $workEmail
      workPhoneNumber: $workPhoneNumber
      divisionId: $divisionId
      designationId: $designationId
      line1: $line1
      line2: $line2
      city: $city
      postalCode: $postalCode
      province: $province
      country: $country
    ) {
      id
      fileNumber
      organisationCode
      signature
      photo
      workEmail
      workPhoneNumber
      division {
        id
        name
      }
      designation {
        id
        name
      }
      address {
        id
        line1
        line2
        city
        province
        postalCode
        country
      }
    }
  }
`);

const Employee: FC = () => {
  const navigate = useNavigate();

  const { departmentId, userId } = useParams<{ departmentId: string; userId: string }>() as {
    departmentId: string;
    userId: string;
  };

  const { loading, error, data, refetch } = useQuery(EMPLOYEE_QUERY, {
    variables: {
      id: userId,
      departmentId,
    },
  });

  const [upsertEmployee, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_EMPLOYEE_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error || !data || !userId) return <ErrorMessage error={error} refetch={refetch} />;

  return (
    <FormPanel
      loading={updateLoading}
      error={updateError}
      onSubmit={formData => {
        upsertEmployee({
          variables: {
            userId,
            fileNumber: formData.fileNumber,
            organisationCode: formData.organisationCode,
            signature: formData.signature instanceof File ? formData.signature : undefined,
            photo: formData.photo instanceof File ? formData.photo : undefined,
            workEmail: formData.workEmail,
            workPhoneNumber: formData.workPhoneNumber,
            divisionId: formData.divisionId,
            designationId: formData.designation.id,
            line1: formData.line1,
            line2: formData.line2,
            city: formData.city,
            postalCode: formData.postalCode,
            province: formData.province,
            country: formData.country,
          },
        }).then(() => {
          navigate(`/departments/${departmentId}/users`);
        });
      }}
      onCancel={() => {
        navigate(`create/departments/${departmentId}/user/${userId}/basic-information`);
      }}
      submitButtonLabel="Save and continue"
      cancelButtonLabel="Back"
    >
      <h2>Employee Information</h2>
      <FormInput
        fieldName="fileNumber"
        type="string"
        fullWidth
        defaultValue={data.user.employee?.fileNumber || ''}
        label="File Number"
        validators={{
          required: true,
        }}
      />
      {departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e' ? (
        <FormInput
          fieldName="organisationCode"
          type="select"
          fullWidth
          defaultValue={data.user.employee?.organisationCode || 'DOE'}
          label="Organisation"
          options={[
            {
              label: 'Department Of Education',
              value: 'DOE',
            },
            {
              label: 'Teaching Service Commission',
              value: 'TSC',
            },
            {
              label: 'Office of Library and Archive',
              value: 'OLA',
            },
            {
              label: 'Ministry of Education',
              value: 'MOE',
            },
          ]}
          validators={{
            required: true,
          }}
        />
      ) : (
        <></>
      )}
      <Columns number={2}>
        <FormInput
          fieldName="signature"
          type="upload"
          fullWidth
          defaultValue={data.user.employee?.signature || ''}
          label="Signature"
        />
        <FormInput
          fieldName="photo"
          type="upload"
          fullWidth
          defaultValue={data.user.employee?.photo || ''}
          label="Photo"
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2}>
        <FormInput
          fieldName="workEmail"
          type="string"
          fullWidth
          defaultValue={data.user.employee?.workEmail}
          label="Work Email"
        />
        <FormInput
          fieldName="workPhoneNumber"
          type="string"
          fullWidth
          defaultValue={data.user.employee?.workPhoneNumber}
          label="Work Phone Number"
        />
        <FormInput
          fieldName="divisionId"
          type="select"
          fullWidth
          defaultValue={data.user?.employee?.division?.id || ''}
          label="Division"
          options={data.department.divisions.map(division => ({
            label: division.name as string,
            value: division.id as string,
          }))}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="custom"
          fieldName="designation"
          label="Designation"
          defaultValue={data.user.employee?.designation || ''}
          customInput={SearchDesignations}
          validators={{
            required: true,
          }}
        />
        {/* <FormInput
          fieldName="designationId"
          type="select"
          fullWidth
          defaultValue={data.user.employee?.designation.id || ''}
          label="Designation"
          options={data.department.designations.nodes.map(designation => ({
            label: designation.name,
            value: designation.id,
          }))}
          validators={{
            required: true,
          }}
        /> */}
      </Columns>
      <FormInput
        fieldName="line1"
        type="string"
        fullWidth
        defaultValue={data.user.employee?.address?.line1 || ''}
        label="Line 1"
        validators={{
          required: true,
        }}
      />
      <FormInput
        fieldName="line2"
        type="string"
        fullWidth
        defaultValue={data.user.employee?.address?.line2 || ''}
        label="Line 2"
      />
      <Columns number={2}>
        <FormInput
          fieldName="city"
          type="string"
          fullWidth
          defaultValue={data.user.employee?.address?.city || ''}
          label="City"
          validators={{
            required: true,
          }}
        />
        <FormInput
          fieldName="province"
          type="select"
          fullWidth
          defaultValue={data.user.employee?.address?.province || ''}
          label="Province"
          validators={{
            required: true,
          }}
          options={Object.keys(provinces).map(province => ({
            label: provinces[province],
            value: province,
          }))}
        />
        <FormInput
          fieldName="postalCode"
          type="string"
          fullWidth
          defaultValue={data.user.employee?.address?.postalCode || ''}
          label="Postal Code"
          validators={{
            required: true,
          }}
        />
        <FormInput
          fieldName="country"
          type="select"
          fullWidth
          defaultValue={data.user.employee?.address?.country || 'PG'}
          label="Country"
          options={Object.keys(countries).map(country => ({
            label: countries[country],
            value: country,
          }))}
          validators={{
            required: true,
          }}
        />
      </Columns>
    </FormPanel>
  );
};

export default Employee;
